import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Users, Edit, Trash2, Eye, EyeOff, CheckCircle, XCircle, 
  Loader2, Search, Star, Shield, Phone, Mail, MapPin, Crown, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';
import ProfessionalEditor from './ProfessionalEditor';

export default function AdminProfessionals() {
  var [professionals, setProfessionals] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  var [sortBy, setSortBy] = useState('recent');
  var [editingProfessional, setEditingProfessional] = useState(null);

  useEffect(function() {
    loadProfessionals();
  }, []);

  async function loadProfessionals() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/professionals?limit=200', { withCredentials: true });
      setProfessionals(res.data.professionals || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function handleDelete(userId) {
    if (!window.confirm('¿Eliminar este profesional?')) return;
    try {
      await authAxios.delete(API + '/admin/professionals/' + userId, { withCredentials: true });
      toast.success('Profesional eliminado');
      loadProfessionals();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  async function toggleVisibility(userId, isPublic) {
    try {
      await authAxios.put(API + '/admin/professionals/' + userId, { is_public: !isPublic }, { withCredentials: true });
      toast.success(isPublic ? 'Profesional ocultado' : 'Profesional visible');
      loadProfessionals();
    } catch (e) {
      toast.error('Error');
    }
  }

  async function toggleVerified(userId, isVerified) {
    try {
      await authAxios.put(API + '/admin/professionals/' + userId, { is_verified: !isVerified }, { withCredentials: true });
      toast.success(isVerified ? 'Verificación removida' : 'Profesional verificado');
      loadProfessionals();
    } catch (e) {
      toast.error('Error');
    }
  }

  var filteredProfessionals = professionals.filter(function(p) {
    if (!searchTerm) return true;
    var term = searchTerm.toLowerCase();
    return (p.name && p.name.toLowerCase().includes(term)) ||
           (p.specialty && p.specialty.toLowerCase().includes(term)) ||
           (p.city && p.city.toLowerCase().includes(term)) ||
           (p.email && p.email.toLowerCase().includes(term));
  });

  // Sort
  filteredProfessionals = [...filteredProfessionals].sort(function(a, b) {
    if (sortBy === 'recent') {
      var dateA = a.created_at || a.updated_at || '';
      var dateB = b.created_at || b.updated_at || '';
      return dateB.localeCompare(dateA);
    } else if (sortBy === 'name') {
      return (a.name || '').localeCompare(b.name || '');
    } else if (sortBy === 'verified') {
      return (b.is_verified ? 1 : 0) - (a.is_verified ? 1 : 0);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-2 flex-1 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar profesionales..."
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
              data-testid="search-professionals"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px] bg-slate-800 border-slate-700 text-slate-300" data-testid="sort-professionals">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="recent" className="text-slate-300">Más recientes</SelectItem>
              <SelectItem value="name" className="text-slate-300">Nombre A-Z</SelectItem>
              <SelectItem value="verified" className="text-slate-300">Verificados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{professionals.length}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{professionals.filter(function(p) { return p.is_public !== false; }).length}</p>
              <p className="text-slate-400 text-sm">Públicos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{professionals.filter(function(p) { return p.is_verified; }).length}</p>
              <p className="text-slate-400 text-sm">Verificados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">{professionals.filter(function(p) { return p.specialty; }).length}</p>
              <p className="text-slate-400 text-sm">Con Especialidad</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Form - Full Page Editor */}
      {editingProfessional && (
        <div className="fixed inset-0 z-50 bg-slate-950">
          <ProfessionalEditor 
            professional={editingProfessional}
            onSave={function() { setEditingProfessional(null); loadProfessionals(); }}
            onCancel={function() { setEditingProfessional(null); }}
          />
        </div>
      )}

      {/* Professionals List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Profesionales ({filteredProfessionals.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredProfessionals.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay profesionales</p>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredProfessionals.map(function(prof) {
                return (
                  <div key={prof.user_id || prof._id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-white font-medium truncate">{prof.name || 'Sin nombre'}</h4>
                          {prof.is_verified && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                              <Shield className="w-3 h-3" /> Verificado
                            </span>
                          )}
                          {!prof.is_public && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                              Oculto
                            </span>
                          )}
                        </div>
                        {prof.specialty && (
                          <p className="text-emerald-400 text-sm mt-1">{prof.specialty}</p>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-400">
                          {prof.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {prof.city}
                            </span>
                          )}
                          {prof.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {prof.phone}
                            </span>
                          )}
                          {prof.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {prof.email}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={function() { toggleVisibility(prof.user_id, prof.is_public !== false); }}
                          className="text-slate-400 hover:text-white"
                        >
                          {prof.is_public !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={function() { toggleVerified(prof.user_id, prof.is_verified); }}
                          className={prof.is_verified ? 'text-blue-400' : 'text-slate-400'}
                        >
                          <Shield className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={function() { setEditingProfessional(prof); }}
                          className="text-slate-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={function() { handleDelete(prof.user_id); }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
